import { formatPercent, shareOf, sumCounts } from './overviewFormat';

export interface BarSegment {
    key: string;
    label: string;
    count: number;
    swatch: string;
}

interface StackedBarProps {
    segments: readonly BarSegment[];
    // Defaults to the segments' sum; a larger total leaves the rest of the track empty.
    total?: number;
    // Base for the hover percentage when it differs from the width scale; defaults to it.
    percentBase?: number;
    heightClassName?: string;
    label: string;
}

const SEGMENT_CLASS = 'h-full transition-[width] duration-500 ease-out';

export function StackedBar({
    segments,
    total,
    percentBase,
    heightClassName = 'h-3',
    label
}: StackedBarProps) {
    const base = total ?? sumCounts(segments);
    const hoverBase = percentBase ?? base;
    const summary = segments.map((s) => `${s.label} ${s.count}`).join(', ');
    return (
        <div
            role="img"
            aria-label={`${label}: ${summary}`}
            className={`flex w-full overflow-hidden rounded-full bg-gray-100 ${heightClassName}`}
        >
            {segments.map((segment) => (
                <span
                    key={segment.key}
                    title={`${segment.label}: ${segment.count} (${formatPercent(segment.count, hoverBase)})`}
                    className={`${SEGMENT_CLASS} ${segment.swatch}`}
                    style={{ width: `${shareOf(segment.count, base)}%` }}
                />
            ))}
        </div>
    );
}

export function Swatch({ className }: { className: string }) {
    return <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ${className}`} />;
}

interface SwatchLegendProps {
    items: readonly { key: string; label: string; swatch: string; count?: number }[];
}

export function SwatchLegend({ items }: SwatchLegendProps) {
    return (
        <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-xs text-gray-700">
            {items.map((item) => (
                <span key={item.key} className="inline-flex items-center gap-1.5">
                    <Swatch className={item.swatch} />
                    {item.label}
                    {item.count !== undefined && (
                        <span className="font-semibold tabular-nums text-gray-900">
                            {item.count.toLocaleString()}
                        </span>
                    )}
                </span>
            ))}
        </div>
    );
}

export function EmptyNote({ children }: { children: string }) {
    return <p className="py-6 text-center text-sm text-gray-400">{children}</p>;
}

export function CardMeta({ children }: { children: string }) {
    return <span className="shrink-0 text-xs text-gray-500">{children}</span>;
}
